import { useEffect, useState } from "react";
import {
  ensureBarcodeCameraPermission,
  scanBarcodeOnce,
  stopBarcodeScan,
} from "../util/BarcodeScan";

const CAMERA_TIMEOUT_IN_MS = 10000; //10 seconds

// ✅ Check if camera permissions are granted
const checkCameraPermission = async (): Promise<boolean> => {
  const granted = await ensureBarcodeCameraPermission();

  if (!granted) {
    console.warn("Camera permission denied");
    return false;
  }

  return true;
};

export function useBarcodeManager(onScan?: (content: string) => void) {
  const [scanText, setScanText] = useState<string>("");
  const [honeywellFallback, setHoneywellFallback] = useState(false);

  const stopScan = async () => {
    setHoneywellFallback(false);
    try {
      // ✅ ADD: Error handling
      await stopBarcodeScan();
    } catch (error) {
      console.error("Error stopping scan:", error);
    }
  };

  /**
   * Honeywell CT40 scanners typically work in two modes:
      1. Keyboard Wedge Mode (Default) → Scans act as keyboard input
      2. Intent API Mode → Sends scans via Android intents
    We will listen for both as a fallback.
   */

  // ✅ Listen for Honeywell Scanner Input (Keyboard Wedge Mode)
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === "Enter" && scanText) {
        console.log("Honeywell Scanner Data:", scanText);
        onScan?.(scanText); // ✅ ADD: Actually process the data
        setScanText("");
      } else if (event.key.length === 1) {
        setScanText((prev) => prev + event.key);
      }
    };

    if (honeywellFallback) {
      window.addEventListener("keydown", handleKeyPress);
    }

    return () => {
      window.removeEventListener("keydown", handleKeyPress);
    };
  }, [scanText, honeywellFallback, onScan]);

  // ✅ Listen for Honeywell Scanner (Intent API Mode)
  useEffect(() => {
    const handleHoneywellScan = (event: any) => {
      try {
        // ✅ ADD: Error handling
        const url = new URL(event.url);
        const barcodeValue = url.searchParams.get("barcode");
        if (barcodeValue) {
          setScanText(barcodeValue);
          onScan?.(barcodeValue);
          stopScan();
        }
      } catch (error) {
        console.error("Error parsing app URL:", error);
      }
    };

    window.addEventListener("scan", handleHoneywellScan);

    return () => {
      window.removeEventListener("scan", handleHoneywellScan);
    };

    // const listener = App.addListener("appUrlOpen", handleAppUrlOpen);

    // return () => { // ✅ ADD: Cleanup
    //   listener?.remove();
    // };
  }, [onScan]);

  async function scanClick(): Promise<void> {
    try {
      await startScan(); // ✅ Add await
    } catch (error) {
      console.error("Error in scanClick:", error);
      enableHoneywellFallback();
    }
  }

  // ✅ Enable Honeywell Fallback (Hardware Scanner)
  const enableHoneywellFallback = () => {
    setHoneywellFallback(true);
  };

  const startScan = async () => {
    const hasPermission = await checkCameraPermission();
    if (!hasPermission) {
      console.warn("Camera scanning not permitted. Falling back...");
      enableHoneywellFallback();
      return;
    }

    try {
      // ✅ Resolves null on timeout (camera did not scan anything) or cancel
      const content = await scanBarcodeOnce(CAMERA_TIMEOUT_IN_MS);

      if (content) {
        setScanText(content);
        onScan?.(content);
        await stopScan();
      } else {
        console.log(
          "Scan timeout or failure. Stopping camera scanner and enabling honeywell fallback"
        );
        await stopScan();
        enableHoneywellFallback();
      }
    } catch (error) {
      console.log("Scanning error:", error);
      await stopScan();
      enableHoneywellFallback();
    }
  };

  return {
    scanText,
    setScanText,
    scanClick,
    stopScan,
  };
}
